/**
 * RunFastCleanupPanel — NC-1168 (Slice 3, retro path).
 *
 * Lets the tutor flip "Testlauf: direkt nach Ende aufräumen" on an EXISTING run.
 * `RunCreateDialog` covers the opt-in at creation time; this is the only way to
 * change it afterwards.
 *
 * The BE (`runs-fastcleanup-toggle`) answers with `cleanupImminent` when the
 * run's end + grace has ALREADY passed — i.e. the very next `runs-cleanup-due`
 * sweep will collect the project files and delete the run. That case is the
 * whole reason this panel exists as more than a checkbox: switching the option
 * on for a run that ended yesterday is effectively "delete this soon", and the
 * tutor must be told, not surprised.
 */

import React, { useState } from 'react';
import {
  Alert,
  Box,
  Card,
  CardContent,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  Typography,
} from '@mui/material';
import { runsApi } from '../services/api';

interface RunFastCleanupPanelProps {
  runId: string;
  /** Current persisted opt-in state (absent/false = today's retention behaviour). */
  fastCleanup?: boolean;
  /** operator+ — viewers see the state read-only. */
  canOperate: boolean;
  /** Re-fetch the run so the parent's copy matches what was just persisted. */
  onChanged?: () => void;
}

export function RunFastCleanupPanel({
  runId,
  fastCleanup,
  canOperate,
  onChanged,
}: RunFastCleanupPanelProps) {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [imminent, setImminent] = useState<{ endUtc: string | null; graceMinutes: number } | null>(
    null,
  );

  const enabled = fastCleanup === true;

  const handleToggle = async (next: boolean) => {
    // Guard the handler, not just the control: `disabled` is a rendering hint a
    // viewer could bypass, and this endpoint mutates the run's teardown schedule.
    if (!canOperate || saving) return;
    setSaving(true);
    setError(null);
    // A fresh decision — drop any warning from a previous toggle.
    setImminent(null);
    try {
      const result = await runsApi.setFastCleanup(runId, next);
      if (result.cleanupImminent) {
        setImminent({ endUtc: result.endUtc, graceMinutes: result.graceMinutes });
      }
      onChanged?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Aufräum-Option konnte nicht geändert werden.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Aufräumen nach Ende
        </Typography>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={enabled}
                disabled={!canOperate || saving}
                onChange={(e) => handleToggle(e.target.checked)}
                inputProps={{ 'aria-label': 'Testlauf: direkt nach Ende aufräumen' }}
              />
            }
            label="Testlauf: direkt nach Ende aufräumen"
          />
          {saving && <CircularProgress size={18} />}
        </Box>

        <Typography variant="body2" color="text.secondary">
          {enabled
            ? 'Projektdateien werden eingesammelt und der Lauf wird kurz nach dem Ende gelöscht — keine Aufbewahrungsfrist.'
            : 'Der Lauf bleibt bis zum Ablaufdatum bestehen und wird erst vom täglichen Sweep aufgeräumt.'}
        </Typography>

        {imminent && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            Das Ende dieses Laufs liegt bereits {imminent.graceMinutes} Minuten oder länger zurück
            {imminent.endUtc ? ` (Ende: ${new Date(imminent.endUtc).toLocaleString('de-DE')})` : ''}
            {' '}— der nächste Aufräum-Lauf sammelt die Projektdateien ein und löscht den Lauf.
            Wieder abwählen, falls das nicht gewollt ist.
          </Alert>
        )}

        {error && (
          <Alert severity="error" sx={{ mt: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
      </CardContent>
    </Card>
  );
}

export default RunFastCleanupPanel;
